// packages/utils/src/validators.ts

/**
 * Validates a Brazilian CPF (11 digits, 2 check digits).
 * Implements the full Receita Federal mod-11 verification digit algorithm.
 * @example isValidCPF("123.456.789-09") -> true
 * @example isValidCPF("12345678909") -> true
 * @example isValidCPF("111.111.111-11") -> false
 */
export function isValidCPF(cpf: string): boolean {
  const cleaned = cpf.replace(/[^\d]/g, '');

  if (cleaned.length !== 11) return false;

  // Reject known invalid patterns (all same digits)
  if (/^(\d)\1{10}$/.test(cleaned)) return false;

  // Calculate first check digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i), 10) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;

  if (parseInt(cleaned.charAt(9), 10) !== remainder) return false;

  // Calculate second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i), 10) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;

  return parseInt(cleaned.charAt(10), 10) === remainder;
}

/**
 * Validates a Brazilian CNPJ (14 digits, 2 check digits).
 * Implements the full Receita Federal mod-11 with multipliers algorithm.
 * @example isValidCNPJ("11.222.333/0001-81") -> true
 * @example isValidCNPJ("11222333000181") -> true
 * @example isValidCNPJ("00000000000000") -> false
 */
export function isValidCNPJ(cnpj: string): boolean {
  const cleaned = cnpj.replace(/[^\d]/g, '');

  if (cleaned.length !== 14) return false;

  // Reject known invalid patterns (all same digits)
  if (/^(\d)\1{13}$/.test(cleaned)) return false;

  // Calculate first check digit
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleaned.charAt(i), 10) * (weights1[i] ?? 0);
  }
  let remainder = sum % 11;
  const firstCheckDigit = remainder < 2 ? 0 : 11 - remainder;

  if (parseInt(cleaned.charAt(12), 10) !== firstCheckDigit) return false;

  // Calculate second check digit
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleaned.charAt(i), 10) * (weights2[i] ?? 0);
  }
  remainder = sum % 11;
  const secondCheckDigit = remainder < 2 ? 0 : 11 - remainder;

  return parseInt(cleaned.charAt(13), 10) === secondCheckDigit;
}

/**
 * Validates an email address.
 * Uses a practical regex that covers 99%+ of valid email addresses.
 * @example isValidEmail("user@example.com") -> true
 * @example isValidEmail("not-an-email") -> false
 */
export function isValidEmail(email: string): boolean {
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email);
}

/**
 * Validates a Brazilian phone number (with DDD).
 * Accepts formats: (11)91234-5678, 11912345678, +5511912345678
 * @example isValidPhone("(11) 91234-5678") -> true
 * @example isValidPhone("11912345678") -> true
 * @example isValidPhone("12345") -> false
 */
export function isValidPhone(phone: string): boolean {
  const cleaned = phone.replace(/[^\d]/g, '');

  // With country code: 13 digits (55 + DDD + 9 digits)
  if (cleaned.length === 13 && cleaned.startsWith('55')) return true;

  // Without country code: 11 digits (DDD + 9 digits for mobile)
  if (cleaned.length === 11) return true;

  // Landline: 10 digits (DDD + 8 digits)
  if (cleaned.length === 10) return true;

  return false;
}

/**
 * Validates a Brazilian vehicle license plate.
 * Supports both old format (ABC-1234) and Mercosul format (ABC1D23).
 * @example isValidPlate("ABC-1234") -> true
 * @example isValidPlate("ABC1D23") -> true
 * @example isValidPlate("abc1d23") -> true (case-insensitive)
 * @example isValidPlate("12345") -> false
 */
export function isValidPlate(plate: string): boolean {
  const cleaned = plate.replace(/[-\s]/g, '').toUpperCase();

  // Old format: 3 letters + 4 digits (ABC1234)
  const oldFormat = /^[A-Z]{3}\d{4}$/;

  // Mercosul format: 3 letters + 1 digit + 1 letter + 2 digits (ABC1D23)
  const mercosulFormat = /^[A-Z]{3}\d[A-Z]\d{2}$/;

  return oldFormat.test(cleaned) || mercosulFormat.test(cleaned);
}

/**
 * Sentinela que a própria tabela FIPE usa como ano-modelo de veículo 0 km
 * (`year_code = 32000-x`). Não é um ano de calendário — é um marcador.
 */
export const FIPE_ZERO_KM_YEAR_MODEL = 32000;

/**
 * O ano de fabricação não pode ser posterior ao ano-modelo: a montadora produz
 * no ano do modelo ou no anterior (fabricação 2025 / modelo 2026), nunca depois.
 * Um 0 km com ano-modelo REAL fica sujeito à mesma regra — ela não some por ser
 * 0 km.
 *
 * Devolve `true` quando não há o que comparar: ano ausente dos dois lados, ou
 * ano-modelo igual ao sentinela 0 km. Esse último descarte é explícito e hoje
 * não muda resultado nenhum (nenhum ano plausível é maior que 32000) — existe
 * porque o sentinela não é uma data, e uma regra futura em FAIXA
 * (`modelo - 1 ≤ fab ≤ modelo`) reprovaria todo 0 km sem ele.
 */
export function isManufactureYearConsistent(
  manufactureYear: number | null | undefined,
  modelYear: number | null | undefined,
): boolean {
  if (typeof manufactureYear !== 'number' || !Number.isFinite(manufactureYear)) return true;
  if (typeof modelYear !== 'number' || !Number.isFinite(modelYear)) return true;
  if (modelYear === FIPE_ZERO_KM_YEAR_MODEL) return true;
  return manufactureYear <= modelYear;
}
